import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { auth, authReady } from '../../services/firebase';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { saveSession } from '../../services/autoLogin';
import { translations } from '../../i18n';
import './login.css';

export const Login = () => {
  const navigate = useNavigate();
  const lang = (localStorage.getItem('lang') as 'ES' | 'EN') || 'ES';
  const l = translations[lang].login;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleEmailLogin = async () => {
    setError('');
    if (!email || !password) { setError(l.errorEmpty); return; }
    try {
      setLoading(true);
      await authReady;
      await signInWithEmailAndPassword(auth, email, password);
      // Guarda credenciales para login silencioso en caso de que
      // Android limpie la sesión de Firebase
      saveSession(email, password);
      navigate('/chat');
    } catch { setError(l.errorCredentials); }
    finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    try {
      setLoading(true);
      await authReady;
      await signInWithPopup(auth, new GoogleAuthProvider());
      // Google no guarda contraseña — Firebase maneja su propio token
      navigate('/chat');
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async () => {
    setError('');
    if (!email) { setError(l.resetEmailError); return; }
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch { setError(l.resetNotFound); }
    finally { setLoading(false); }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo" onClick={() => navigate('/')}>TolkX</div>
        <h1 className="login-title">{l.title}</h1>

        {error && <div className="login-error">{error}</div>}
        {resetSent && <div className="login-success">{l.resetSuccess}</div>}

        <div className="login-field">
          <label className="login-label">{l.email}</label>
          <input className="login-input" type="email" placeholder={l.emailPlaceholder} value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleEmailLogin()} />
        </div>

        <div className="login-field">
          <div className="login-label-row">
            <label className="login-label">{l.password}</label>
            <span className="login-forgot" onClick={handleForgotPassword}>{l.forgot}</span>
          </div>
          <div className="login-input-wrap">
            <input className="login-input" type={showPassword ? 'text' : 'password'} placeholder={l.passwordPlaceholder} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleEmailLogin()} />
            <button className="login-toggle" type="button" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button className="login-submit" onClick={handleEmailLogin} disabled={loading}>
          {loading ? l.loading : l.submit}
        </button>

        <div className="login-divider"><span>{l.or}</span></div>

        <button className="login-social-btn" onClick={handleGoogle} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {l.google}
        </button>

        <p className="login-register">
          {l.noAccount}{' '}
          <span className="login-link" onClick={() => navigate('/register')}>{l.createAccount}</span>
        </p>
      </div>
    </div>
  );
};